import {
  PaginatedResponse,
  User,
  CreateUserBody,
  UsersQueryParams,
  UpdateUserBody,
  type UserPreferences,
} from '@eppendorf/vnls-user-tenant-utils';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { fetcher } from '$shared/fetch';
import {
  createPathWithQueryParams,
  createUserPath,
  objectToURLSearchParams,
  REPORT_VERIFICATION,
  START_VERIFICATION,
  USERS_BASE_PATH,
} from '$shared/utils/api-paths';

import {
  ReportVerificationBody,
  StartVerificationBody,
} from '$features/show-user/verify-phone-number-dialog/types';

export const userQueryKey = 'users';
export const preferencesQueryKey = 'preferences';

export const createUsersQueryWithQueryParams = (queryParams: UsersQueryParams) => ({
  queryKey: [userQueryKey, ...objectToURLSearchParams(queryParams).values()],
  queryFn: () =>
    fetcher.get<PaginatedResponse<User>>(
      createPathWithQueryParams(USERS_BASE_PATH, queryParams),
    ),
  placeholderData: keepPreviousData,
});
export const useUsersWithQueryParams = (queryParams: UsersQueryParams) =>
  useQuery(createUsersQueryWithQueryParams(queryParams));

export const createGetUserQuery = (id: string) => ({
  queryKey: [userQueryKey, id],
  queryFn: () => fetcher.get<User>(createUserPath(id)),
  enabled: !!id,
});
export const useGetUser = (id: string) => useQuery(createGetUserQuery(id));

export const useAddUser = () => {
  const addUser = (user: CreateUserBody) =>
    fetcher.post<CreateUserBody, User>(USERS_BASE_PATH, user);

  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<User>, AxiosError, CreateUserBody>({
    mutationFn: addUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [userQueryKey] });
    },
  });
};

export const useUpdateUser = () => {
  const updateUser = (user: UpdateUserBody & Pick<User, 'id'>) => {
    const { id, ...userWithoutId } = user;
    return fetcher.patch<UpdateUserBody, User>(createUserPath(id), userWithoutId);
  };

  const queryClient = useQueryClient();
  return useMutation<User, AxiosError, UpdateUserBody & Pick<User, 'id'>>({
    mutationFn: updateUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [userQueryKey] });

      queryClient.setQueryData([userQueryKey, data.id], data);
    },
  });
};

export const useDeleteUser = () => {
  const deleteUser = (id: string) => fetcher.delete(`${USERS_BASE_PATH}/${id}`);
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse, AxiosError, string>({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [userQueryKey] });
    },
  });
};

export const useResendInvite = () => {
  const updateInviteSendAt = (id: string) =>
    fetcher.post<unknown, User>(
      `${USERS_BASE_PATH}/${id}/actions/resend-invite`,
      undefined,
    );

  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<User>, AxiosError, string>({
    mutationFn: updateInviteSendAt,
    onSuccess: (/* data */) => {
      queryClient.invalidateQueries({ queryKey: [userQueryKey] });

      // queryClient.setQueryData([userQueryKey, data.data.id], data);
    },
    onError: (error) => {
      // eslint-disable-next-line no-console -- for debugging
      console.log(error);
    },
  });
};

export const useStartPhoneVerification = () => {
  const startPhoneVerification = (startVerificationBody: StartVerificationBody) =>
    fetcher.post<unknown, StartVerificationBody>(
      `${START_VERIFICATION}`,
      startVerificationBody,
    );

  return useMutation<
    AxiosResponse<StartVerificationBody>,
    AxiosError,
    StartVerificationBody
  >({
    mutationFn: startPhoneVerification,
  });
};

export const useReportPhoneVerification = () => {
  const reportPhoneVerification = (reportVerificationBody: ReportVerificationBody) =>
    fetcher.post<unknown, ReportVerificationBody>(
      `${REPORT_VERIFICATION}`,
      reportVerificationBody,
    );

  const queryClient = useQueryClient();

  return useMutation<
    AxiosResponse<ReportVerificationBody>,
    AxiosError,
    ReportVerificationBody
  >({
    mutationFn: reportPhoneVerification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [userQueryKey] });
    },
  });
};

export const useGetUserPreferences = (userId: string) =>
  useQuery({
    queryKey: [userId, preferencesQueryKey],
    queryFn: () =>
      fetcher.get<UserPreferences>(`${USERS_BASE_PATH}/${userId}/preferences`),
  });

export const useUpdateUserPreferences = (userId: string) => {
  const queryClient = useQueryClient();
  const updateUserPreferences = (preferences: UserPreferences) =>
    fetcher.patch<UserPreferences, UserPreferences>(
      `${USERS_BASE_PATH}/${userId}/preferences`,
      preferences,
    );

  return useMutation<UserPreferences, AxiosError, UserPreferences>({
    mutationFn: updateUserPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [userId, preferencesQueryKey] });
    },
  });
};
